import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'

// Components
import { Button, If, Unless, Page, BotHeadSpin } from '../../../../..'
import Text from '../../../../Text'
import HomeSalesSvg from '../shared/HomeSalesSvg'
import { IconWrench } from '../../../../Icon'
import { RedirectToHomeDigest } from '../../../../Navigation'
import { Player } from '../../../../Player'

// Selectors
import { selectClientFetchId } from '../../../../../../store/selectors/client'
import { selectHomeId, selectZipcode } from '../../../../../../store/selectors/home'
import { selectHomeownerMarketLoading } from '../../../../../../store/selectors/marketRecord'
import { selectShouldRenderAvmHotMarketPanel } from '../../../../../../store/selectors/display'
import {
  selectYourHomeValueVideoId,
  selectYourHomeValueVideoType,
} from '../../../../../../store/selectors/videos'

// Utils
import { OVERLAY_HELPERS, PATH_HELPERS } from '../../../../../../constants/navigation'
import { dispatchEvent } from '../../../../../../helpers/tagManager'
import {
  HotMarketWarningTuneValueClick,
  HotMarketWarningYourMarketClick,
  HotMarketWarningLearnMoreLoad,
} from '../../../../../../constants/trackingEvents'
import history from '../../../../../../history'
import LowInventorySvg from './LowInventorySvg'

// Styles
import styles from './AvmHotMarketWarning.module.scss'

const handleNavigation = newPath => {
  history.push(newPath)
}

const AvmHotMarketWarning = () => {
  const clientId = useSelector(selectClientFetchId)
  const homeId = useSelector(selectHomeId)
  const zipcode = useSelector(selectZipcode)

  const yourHomeValueVideoId = useSelector(selectYourHomeValueVideoId)
  const yourHomeValueVideoType = useSelector(selectYourHomeValueVideoType)

  const shouldRender = useSelector(selectShouldRenderAvmHotMarketPanel)
  const loading = useSelector(selectHomeownerMarketLoading)

  const handleTuneYourValueNavigation = () => {
    dispatchEvent(HotMarketWarningTuneValueClick)
    handleNavigation(PATH_HELPERS.tuneYourValue.buildPath(clientId, homeId))
  }

  const handleExploreMarketNavigation = () => {
    dispatchEvent(HotMarketWarningYourMarketClick)
    handleNavigation(OVERLAY_HELPERS.marketSpotlight.buildPath())
  }

  useEffect(() => {
    dispatchEvent(HotMarketWarningLearnMoreLoad)
  }, [])

  if (!shouldRender) {
    return <RedirectToHomeDigest />
  }

  return (
    <Page>
      <Page.Body dataQa='avm-hot-market-panel'>
        <If condition={loading}>
          <BotHeadSpin panelSpacing />
        </If>
        <Unless condition={loading}>
          <div>
            <section className={styles.section}>
              <Text
                element='h4'
                variant='neutralCool'
                variantCount='900'
                size='l'
                data-qa='avm-hot-market-panel-header'
              >
                <FormattedMessage
                  id='Homeowner.avmHotMarketWarning.title'
                  defaultMessage='{icon} {zipcode} is in high demand which could make your valuations change quickly'
                  values={{
                    zipcode,
                    icon: '📈',
                  }}
                />
              </Text>
            </section>

            <If condition={yourHomeValueVideoId && yourHomeValueVideoType}>
              <section className={styles.section}>
                <Player
                  videoId={yourHomeValueVideoId}
                  videoType={yourHomeValueVideoType}
                />
              </section>
            </If>

            <section className={styles.section}>
              <div className={styles.wrapper}>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <Text
                      element='h5'
                      variant='neutralCool'
                      variantCount='900'
                      size='l'
                    >
                      <FormattedMessage
                        id='Homeowner.avmHotMarketWarning.subTitle'
                        defaultMessage='A few reasons this might happen'
                      />
                    </Text>
                  </div>
                </div>

                <div className={styles.row}>
                  <div className={classnames(styles.column, styles.column1)}>
                    <div className={styles.lowInventory}>
                      <LowInventorySvg />
                    </div>
                  </div>

                  <div className={classnames(styles.column, styles.column2)}>
                    <div>
                      <Text
                        element='p'
                        variant='neutralCool'
                        variantCount='900'
                        size='m'
                      >
                        <FormattedMessage
                          id='Homeowner.avmHotMarketWarning.firstReason'
                          defaultMessage='Low inventory'
                        />
                      </Text>
                      <Text
                        element='p'
                        variant='neutralCool'
                        variantCount='600'
                        size='s'
                      >
                        <FormattedMessage
                          id='Homeowner.avmHotMarketWarning.firstReasonExp'
                          defaultMessage="When inventory doesn't meet demand, it drives prices up"
                        />
                      </Text>
                    </div>
                  </div>
                </div>

                <div className={styles.row}>
                  <div className={classnames(styles.column, styles.column1)}>
                    <div className={styles.saleSpeed}>
                      <HomeSalesSvg />
                    </div>
                  </div>

                  <div className={classnames(styles.column, styles.column2)}>
                    <div>
                      <Text
                        element='p'
                        variant='neutralCool'
                        variantCount='900'
                        size='m'
                      >
                        <FormattedMessage
                          id='Homeowner.avmHotMarketWarning.secondReason'
                          defaultMessage='Speed of sales'
                        />
                      </Text>
                      <Text
                        element='p'
                        variant='neutralCool'
                        variantCount='600'
                        size='s'
                      >
                        <FormattedMessage
                          id='Homeowner.avmHotMarketWarning.secondReasonExp'
                          defaultMessage='Rapid sales can cause values to dramatically change on a daily basis'
                        />
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div>
                <Text
                  element='p'
                  variant='neutralCool'
                  variantCount='600'
                  size='s'
                >
                  <FormattedMessage
                    id='Homeowner.avmHotMarketWarning.footerCta'
                    defaultMessage='You could tune your value towards the higher end, or {exploreYourMarketLink} to learn more.'
                    values={{
                      exploreYourMarketLink: (
                        <Button
                          data-qa='explore-your-market-link'
                          minimal
                          element='a'
                          onClick={handleExploreMarketNavigation}
                        >
                          <FormattedMessage
                            id='Homeowner.avmHotMarketWarning.footerCtaExploreLink'
                            defaultMessage='explore your market'
                          />
                        </Button>
                      ),
                    }}
                  />
                </Text>
              </div>
            </section>

            <section className={styles.section}>
              <Button
                iconStart={IconWrench}
                secondary
                onClick={handleTuneYourValueNavigation}
              >
                <FormattedMessage
                  id='Homeowner.avmHotMarketWarning.footerCtaTuneLink'
                  defaultMessage='Tune Value'
                />
              </Button>
            </section>
          </div>
        </Unless>
      </Page.Body>
    </Page>
  )
}

export default AvmHotMarketWarning
